import {
  arrayToEntityDict,
  type BuildPlatform,
  type LinkedModuleBuilds,
  type ModuleBuild,
  type Platform
} from '@webapp2/shared/model'
import { postGetBody } from '@webapp2/shared/services/api'
import { computed } from 'vue'
import { isString, registeredStateId, state, type ModuleBuildRegistrationState } from '../state'
import { cache } from './cache'
import { ensureById } from './entity-service'
import type { RemoteModuleBuildLaunchData } from './quest-bridge'

function moduleBuildIds(link: LinkedModuleBuilds): string[] {
  return [link.questBuild?.id, link.windowsBuild?.id, link.linuxBuild?.id].filter(isString)
}

export function platformsOf(link: LinkedModuleBuilds): BuildPlatform[] {
  return (
    [
      ['quest', link.questBuild],
      ['windows', link.windowsBuild],
      ['linux', link.linuxBuild]
    ] as const
  )
    .filter(([, build]) => Boolean(build))
    .map(([platform]) => platform)
}

export function platformBuild(link: LinkedModuleBuilds, platform: Platform = state.platform): ModuleBuild | undefined {
  switch (platform) {
    case 'web':
    case 'quest':
      return link.questBuild
    case 'windows':
      return link.windowsBuild
    case 'linux':
      return link.linuxBuild
  }
}

export function platformBuildData(link: LinkedModuleBuilds): RemoteModuleBuildLaunchData | undefined {
  const build = platformBuild(link)
  if (!build) {
    return undefined
  }
  return {
    id: build.id,
    techniqueId: link.techniqueId,
    techniquePath: link.techniquePath,
    uri: build.uri,
    linkedModuleBuildsId: link.id
  }
}

export function seedRegisteredState(link: LinkedModuleBuilds): boolean {
  const build = platformBuildData(link)
  if (!build) {
    return false
  }
  const id = registeredStateId(link.techniquePath)
  const registeredState: ModuleBuildRegistrationState = {
    id,
    type: 'RegisteredState',
    techniquePath: link.techniquePath,
    linkedModuleBuildsId: link.id,
    downloadStatus: { id: build.id, downloadedBytes: 0, totalBytes: 0, state: 'Queued' }
  }
  state.registeredStates[id] = registeredState
  return true
}

export const linkedModuleBuildsByModuleBuildId = computed(() =>
  Object.values(state.linkedModuleBuilds).reduce<{ [moduleBuildId: string]: LinkedModuleBuilds }>((acc, link) => {
    moduleBuildIds(link).forEach(id => {
      acc[id] = link
    })
    return acc
  }, {})
)

export async function ensureLinkedModuleBuilds(linkIds: string[]) {
  const loaded = await ensureById('LinkedModuleBuilds', state.linkedModuleBuilds, linkIds.filter(isString))
  await cache.setItems(loaded)
}

export async function ensureLinkedModuleBuildsByModuleBuildId(moduleBuildIds: string[]) {
  const idsToLoad = [...new Set(moduleBuildIds.filter(isString))].filter(
    id => !(id in linkedModuleBuildsByModuleBuildId.value)
  )
  if (idsToLoad.length === 0) {
    return
  }
  const links = await postGetBody<LinkedModuleBuilds[]>('documents-by-id', {
    type: 'LinkedModuleBuildsByModuleBuildId',
    ids: idsToLoad
  })
  if (!links) {
    return
  }
  Object.assign(state.linkedModuleBuilds, arrayToEntityDict(links))
  await cache.setItems(links)
}
